import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Tag,
  IconButton,
  useDisclosure,
  useToast,
  Input,
  Select,
  HStack,
  VStack,
  Skeleton,
  Text,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import {
  BsPlus,
  BsEye,
  BsPencil,
  BsTrash,
  BsArrowRepeat,
  BsShieldCheck,
  BsCircleFill,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import biometricDeviceService from "../../services/biometricDeviceService";
import DeviceDrawer from "../../components/biometricDevices/DeviceDrawer";

const DevicesList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const cancelRef = React.useRef();

  // Data state
  const [devices, setDevices] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState({});
  const [deleting, setDeleting] = useState(false);

  // Filters state
  const [filters, setFilters] = useState({
    status: "",
    vendor: "",
    location: "",
  });
  const [page, setPage] = useState(1);
  const limit = 10;

  // Color mode values
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Check user permissions (from localStorage or context)
  const hasWritePermission = () => {
    // TODO: Implement actual RBAC check
    const company = JSON.parse(localStorage.getItem("company") || "{}");
    const role = company.role;
    return ["SuperAdmin", "Admin", "HRHead", "HRExecutive"].includes(role);
  };

  // Fetch devices
  useEffect(() => {
    fetchDevices();
  }, [filters, page]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await biometricDeviceService.getDevices(
        filters,
        page,
        limit,
      );
      setDevices(response.data || []);
      setPagination({
        currentPage: response.currentPage || 1,
        totalPages: response.totalPages || 1,
        total: response.total || 0,
      });
    } catch (err) {
      console.error("Error fetching devices:", err);
      setError(err.message || "Failed to load devices");
      toast({
        title: "Error loading devices",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete device
  const deleteDevice = async (id) => {
    try {
      setDeleting(true);
      await biometricDeviceService.deleteDevice(id);
      toast({
        title: "Device deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
      fetchDevices(); // Refresh list
    } catch (err) {
      toast({
        title: "Error deleting device",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  // Manual sync
  const handleSync = async (deviceId) => {
    try {
      setSyncing((prev) => ({ ...prev, [deviceId]: true }));
      const response = await biometricDeviceService.syncDevice(deviceId);
      toast({
        title: "Sync initiated successfully",
        description: response.message || "Device sync in progress",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchDevices(); // Refresh list
    } catch (err) {
      toast({
        title: "Error syncing device",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncing((prev) => ({ ...prev, [deviceId]: false }));
    }
  };

  const handleAddDevice = () => {
    setSelectedDevice(null);
    onOpen();
  };

  const handleEditDevice = (device) => {
    setSelectedDevice(device);
    onOpen();
  };

  const handleViewDevice = (deviceId) => {
    navigate(`/attendance/biometric/${deviceId}`);
  };

  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deviceToDelete) {
      deleteDevice(deviceToDelete._id);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page on filter change
  };

  const handleDeviceSuccess = () => {
    fetchDevices(); // Refresh list after create/update
    onClose();
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "green",
      INACTIVE: "gray",
      MAINTENANCE: "orange",
      DISABLED: "red",
    };
    return colors[status] || "gray";
  };

  const getSyncModeColor = (mode) => {
    return mode === "PULL" ? "blue" : "purple";
  };

  const getHealthIndicator = (device) => {
    if (!device.isOnline) {
      return { color: "red", label: "Offline" };
    }
    if (device.avgResponseTime > 5000) {
      return { color: "yellow", label: "Slow" };
    }
    return { color: "green", label: "Online" };
  };

  const formatDate = (date) => {
    if (!date) return "Never";
    return new Date(date).toLocaleString();
  };

  return (
    <Box p={6}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <BsShieldCheck size={28} />
          <Heading size="lg">Biometric Devices</Heading>
        </HStack>
        {hasWritePermission() && (
          <Button
            leftIcon={<BsPlus size={20} />}
            colorScheme="teal"
            onClick={handleAddDevice}
          >
            Add Device
          </Button>
        )}
      </Flex>

      {/* Filters */}
      <Box
        bg={bgColor}
        p={4}
        borderRadius="md"
        border="1px"
        borderColor={borderColor}
        mb={4}
      >
        <HStack spacing={4} wrap="wrap">
          <Box>
            <Text fontSize="sm" mb={1} fontWeight="medium">
              Status
            </Text>
            <Select
              placeholder="All Status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              w="200px"
            >
              {biometricDeviceService.getDeviceStatuses().map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Select>
          </Box>

          <Box>
            <Text fontSize="sm" mb={1} fontWeight="medium">
              Vendor
            </Text>
            <Select
              placeholder="All Vendors"
              value={filters.vendor}
              onChange={(e) => handleFilterChange("vendor", e.target.value)}
              w="200px"
            >
              {biometricDeviceService.getVendors().map((vendor) => (
                <option key={vendor.value} value={vendor.value}>
                  {vendor.label}
                </option>
              ))}
            </Select>
          </Box>

          <Box>
            <Text fontSize="sm" mb={1} fontWeight="medium">
              Location
            </Text>
            <Input
              placeholder="Search location"
              value={filters.location}
              onChange={(e) => handleFilterChange("location", e.target.value)}
              w="200px"
            />
          </Box>

          {(filters.status || filters.vendor || filters.location) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilters({ status: "", vendor: "", location: "" });
                setPage(1);
              }}
              mt={6}
            >
              Clear Filters
            </Button>
          )}
        </HStack>
      </Box>

      {/* Loading State */}
      {loading && (
        <VStack spacing={4} align="stretch">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height="60px" />
          ))}
        </VStack>
      )}

      {/* Error State */}
      {!loading && error && (
        <Box textAlign="center" py={10}>
          <Text color="red.500" fontSize="lg">
            Error loading devices: {error}
          </Text>
          <Button mt={4} colorScheme="teal" onClick={fetchDevices}>
            Retry
          </Button>
        </Box>
      )}

      {/* Empty State */}
      {!loading && !error && devices.length === 0 && (
        <Box textAlign="center" py={10}>
          <BsShieldCheck size={64} style={{ margin: "0 auto", opacity: 0.3 }} />
          <Text fontSize="xl" mt={4} fontWeight="medium">
            No biometric devices added yet
          </Text>
          <Text color="gray.500" mt={2}>
            Add your first device to start tracking attendance
          </Text>
          {hasWritePermission() && (
            <Button mt={6} colorScheme="teal" onClick={handleAddDevice}>
              Add First Device
            </Button>
          )}
        </Box>
      )}

      {/* Devices Table */}
      {!loading && !error && devices.length > 0 && (
        <>
          <Box
            bg={bgColor}
            borderRadius="md"
            border="1px"
            borderColor={borderColor}
            overflowX="auto"
          >
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Device Name</Th>
                  <Th>Device Code</Th>
                  <Th>Vendor</Th>
                  <Th>Location</Th>
                  <Th>Sync Mode</Th>
                  <Th>Status</Th>
                  <Th>Last Sync</Th>
                  <Th>Health</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {devices.map((device) => {
                  const health = getHealthIndicator(device);
                  return (
                    <Tr key={device._id}>
                      <Td fontWeight="medium">{device.deviceName}</Td>
                      <Td>
                        <code>{device.deviceCode}</code>
                      </Td>
                      <Td>
                        <Badge colorScheme="blue">{device.vendor}</Badge>
                      </Td>
                      <Td>{device.location || "-"}</Td>
                      <Td>
                        <Tag
                          size="sm"
                          colorScheme={getSyncModeColor(device.syncMode)}
                        >
                          {device.syncMode}
                        </Tag>
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(device.status)}>
                          {device.status}
                        </Badge>
                      </Td>
                      <Td fontSize="sm">{formatDate(device.lastSyncAt)}</Td>
                      <Td>
                        <HStack spacing={2}>
                          <BsCircleFill size={10} color={health.color} />
                          <Text fontSize="sm">{health.label}</Text>
                        </HStack>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="View Details">
                            <IconButton
                              icon={<BsEye />}
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewDevice(device._id)}
                            />
                          </Tooltip>
                          {hasWritePermission() && (
                            <>
                              <Tooltip label="Edit">
                                <IconButton
                                  icon={<BsPencil />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditDevice(device)}
                                />
                              </Tooltip>
                              <Tooltip label="Manual Sync">
                                <IconButton
                                  icon={
                                    syncing[device._id] ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <BsArrowRepeat />
                                    )
                                  }
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={() => handleSync(device._id)}
                                  isDisabled={syncing[device._id]}
                                />
                              </Tooltip>
                              <Tooltip label="Delete">
                                <IconButton
                                  icon={<BsTrash />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() => handleDeleteClick(device)}
                                />
                              </Tooltip>
                            </>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Flex justify="space-between" align="center" mt={4}>
              <Text fontSize="sm" color="gray.600">
                Page {pagination.currentPage} of {pagination.totalPages} (
                {pagination.total} total)
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  isDisabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  isDisabled={page >= pagination.totalPages}
                >
                  Next
                </Button>
              </HStack>
            </Flex>
          )}
        </>
      )}

      {/* Device Drawer */}
      <DeviceDrawer
        isOpen={isOpen}
        onClose={onClose}
        device={selectedDevice}
        onSuccess={handleDeviceSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={deleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Device
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete device "
              {deviceToDelete?.deviceName}"? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                ml={3}
                isLoading={deleting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default DevicesList;
